/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import axiosCall from '../../services/axios';
import { useLoanContext } from '../../contexts/LoanContext';
import { useAuthContext } from '../../contexts/AuthContext';
import { isObjEmptyOrNull, isStrEmptyOrNull } from '../UtilityFunctions';

type UseLoanImpoundTotal = {
  loanImpoundTotal: UnknownLoanImpoundTotals;
  loadingLoanImpoundTotal: boolean;
  reloadLoanImpoundTotal: () => void;
};

const useLoanImpoundTotal = (pathNameOrDate = '/list'): UseLoanImpoundTotal => {
  const queryClient = useQueryClient(); // Use the query client directly
  const { token } = useAuthContext();
  const { loanNumber } = useLoanContext();
  const path = useMemo(() => {
    const date =
      typeof pathNameOrDate === 'string'
        ? new Date(pathNameOrDate)
        : new Date(`${pathNameOrDate}`.length === 10 ? pathNameOrDate * 1000 : pathNameOrDate);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      let str = `${pathNameOrDate}`;
      if (isStrEmptyOrNull(str)) return '';
      if (str.startsWith('/')) return str;
      return '/' + str;
    }

    const year = date.getFullYear();

    let month: number | string = date.getMonth() + 1;
    let day: number | string = date.getDate();

    if (month < 10) month = '0' + month;
    if (day < 10) day = '0' + day;

    return `/${year}${month}${day}`;
  }, [pathNameOrDate]);
  const queryKey = useMemo(() => ['loanImpoundTotals', loanNumber, path, token], [loanNumber, path, token]);

  // Fetch communication data using react-query
  const { data, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      try {
        if (!token) return { data: null };
        const request = {
          url: `live/loan/${loanNumber}/impound/total${path}`,
          method: 'GET',
        };
        const config = {
          signal,
        };
        const headers = {
          'Session-Token': token,
        };
        const response = await axiosCall(request, config, headers);
        console.log(`useLoanImpoundTotal.response: `, response);
        if (!isObjEmptyOrNull(response) && Array.isArray(response) && response[0] && 'effectPmtDte' in response[0]) {
          response.sort((item1, item2) => {
            return item1.effectPmtDte - item2.effectPmtDte;
          });
        }
        return { data: response };
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          console.error(`useLoanImpoundTotal.caught: ${error.message}`);
        }
        // Simulated fetch call for testing, TODO: Remove before public release.
        return {
          data: null,
        };
      }
    },
  });

  const reloadLoanImpoundTotal = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    [queryClient, queryKey],
  );

  return {
    loanImpoundTotal: !isLoading && data && 'data' in data && data.data ? data.data : null,
    loadingLoanImpoundTotal: isLoading,
    reloadLoanImpoundTotal,
  };
};

export default useLoanImpoundTotal;
